import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ai_mcat_chat_status,
  setAIMCATChatStatus,
  setLayout, setToggleAiMCATDrawerExternally,
} from "../../../state/reducers/layoutSlice";
import ChatSideDrawer from "../../atoms/ChatSideDrawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { SubjectSelector } from "./components/SubjectSelector";
import { TransitionWarning } from "./components/TransitionWarning";
import { ChatDrawerFooter } from "./components/ChatDrawerFooter";
import { ChatDrawerHeader } from "./components/ChatDrawerHeader";
import { ChatDrawerBody } from "./components/ChatDrawerBody";
import { Typography, Button } from "components/atoms";
import NewChat from "assets/mcat-chat/new-chat.svg";
import McatChatLogo from "assets/mcat-chat/MCAT-chat-logo-mobile.svg";
import McatChatLogoDark from "assets/mcat-chat/mcat-chat-icon-mobile-dark.svg";
import { useScreenSize } from "../../../hooks";
import { MOBILE_WIDTH } from "../../../utils/constants";
import "./index.scss";
import ChatIdleMessage from "./components/ChatIdleMessage";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../molecules/ToolTip";
import NewChatDark from "assets/mcat-chat/new-chat-dark.svg";
import { ReviewSection } from "./components/ReviewSection";
import {
  useLazyGetSubjectsQuery,
  useStartConversationMutation
} from "../../../services/mcatChat/mcatChatConversation";
import {
  MCatChatEndTrackingRequest,
  MCatChatStartConversationResponse
} from "../../../services/mcatChat/types";
import {setMcatChatAuthInfo, setMcatChatSelectedSubject, setMcatChatDoNotShowTransitionWarning, setMcatChatInitialContext, setMcatChatExamInfo, setMcatChatSessionId, setMcatChatCancelPendingTransition, setShowMcatChatErrorModal} from "../../../state/reducers/AuthSlice";
import {useMcatChatStreaming} from "./useMcatChatStreaming";
import {Spinner} from "../../atoms/Icon";
import ChatBubble from "../../atoms/ChatBubble";
import {IdleTimer} from "../../atoms/IdleTimer";
import {
  AI_MCAT_CHAT_MESSAGE_TYPE,
  AI_MCAT_CHAT_STATE,
  AI_MCAT_TEXT_TAGS,
  AIMCATChatProps,
  AIMCATMessage
} from "./constants";
import {
  useProvideFeedbackMutation,
  useTrackChatEndMutation,
  useTrackChatStartMutation
} from "../../../services/mcatChat/mcatChatExternal";
import {AIMCATErrorAlert} from "./ai-mcat-error-alert";
import {decodeToken} from "../../../utils/functions/decodeToken";
import { AI_CHAT_CLOSE, AI_CHAT_NEW_CHAT } from "utils/constants/GoogleTagGUIDs";
import { mixpanelHelper } from "../../../utils/functions/mixPanelHelper";

export const AIMCATChat = ({ theme }: AIMCATChatProps) => {
  const dispatch: any = useAppDispatch();
  
  // Function to send messages back to iframe via MessageBridge
  const sendMessageToIframe = useCallback((messageType: string) => {
    try {

      // Find all iframes that might contain the trigger
      const iframes = document.querySelectorAll('iframe[data-iframe-modal="true"]');

      iframes.forEach((iframe, index) => {
        const iframeWindow = (iframe as HTMLIFrameElement).contentWindow;
        if (iframeWindow) {
          const message = {
            type: messageType,
            timestamp: new Date().toISOString(),
            data: {}
          };
          
          iframeWindow.postMessage(message, '*'); // You might want to specify the exact origin
        }
      });
    } catch (error) {
      console.error('Error sending message to iframe:', error);
    }
  }, []);
  const { aiMCATChatDrawerStatus, toggleAiMCATDrawerExternally } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status, toggleAiMCATDrawerExternally: boolean } }) =>
      state.layout
  );
  const { mcatChatAuthInfo: {mcatChatSession, isMcatChatSessionEnd}, mcatChatSelectedSubject, mcatChatDoNotShowTransitionWarning, mcatChatInitialContext, mcatChatExamInfo, mcatChatSessionId, mcatChatCancelPendingTransition, showMcatChatErrorModal } = useAppSelector((state) => state.auth);

  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [chatState, setChatState] = useState<AI_MCAT_CHAT_STATE>("SELECT_SUBJECT");
  const [messages, setMessages] = useState<AIMCATMessage[]>([]);
  const [lastScrollPositionWhenCollapsed, setLastScrollPositionWhenCollapsed] = useState<number>(0);
  const [chatStartTime, setChatStartTime] = useState<Date | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSessionEnd, setIsSessionEnd] = useState<boolean>(false);
  const [isChatGenerationError, setIsChatGenerationError] = useState<boolean>(false);
  const [hasUserOpenedChatDrawer, setHasUserOpenedChatDrawer] = useState<boolean>(false);
  const [pendingSubjectForTransition, setPendingSubjectForTransition] = useState<string>("");
  const [isPracticeReviewMode, setIsPracticeReviewMode] = useState<boolean>(false);
  const [previousExamInfoBeforeTransition, setPreviousExamInfoBeforeTransition] = useState<{examName: string; section: string; questionNumber: string} | null>(null);
  const [previousIsPracticeReviewMode, setPreviousIsPracticeReviewMode] = useState<boolean>(false);
  // Use ref to track the displayed exam info before any updates
  const currentDisplayedExamInfoRef = useRef<{examName: string; section: string; questionNumber: string}>({ examName: "", section: "", questionNumber: "" });
  const practiceReviewInitialContextRef = useRef<string>("");

  const screen = useScreenSize();
  const payload = decodeToken();

  // this ref use to handle the side drawer
  const chatSideDrawerRef = useRef<any>(null);
  // this ref use to handle the scrolling behaviour
  const chatDrawerBodyRef = useRef<any>(null);
  // this ref tracks the previous drawer status to prevent infinite loops
  const prevDrawerStatusRef = useRef<ai_mcat_chat_status>(aiMCATChatDrawerStatus);
  //define this query to refetch the subjects as lazy query
  const [ triggerMcatChatSubjectsRefetch, mcatChatSubjectsRefetchResult ] = useLazyGetSubjectsQuery();
  const isSubjectsLoading = mcatChatSubjectsRefetchResult.isLoading || (mcatChatSubjectsRefetchResult.isFetching && !mcatChatSubjectsRefetchResult.data);
  //define the mutation for start the chat conversation
  const [startConversation, { isLoading: isLoadingStartConversation }]: any = useStartConversationMutation();
  //define the mutation for start the chat tracking
  const [startChatTracking, { isLoading: isLoadingStartChatTracking }]: any = useTrackChatStartMutation();
  //define the mutation for end the chat tracking
  const [endChatTracking, { isLoading: isLoadingEndChatTracking }]: any = useTrackChatEndMutation();
  //define the mutation for send the feedback
  const [provideFeedback, { isLoading: isLoadingProvideFeedback }]: any = useProvideFeedbackMutation();
  // this hook use to get the streaming chat when send a message
  const { sendUserMessage, aiResponse, isLoadingChatBubble, errorChatBubble, cancelStream } = useMcatChatStreaming();

  // mix panel related info
  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  // this use to identify the mobile or tab screen
  useEffect(() => {
    setIsMobile(screen.width < MOBILE_WIDTH);
  }, [screen.width]);

  // if there is an unexpected error, this will trigger and close the drawer
  useEffect(() => {
    if (isMcatChatSessionEnd && !showMcatChatErrorModal) {
      onCloseDrawer();
    }
  }, [isMcatChatSessionEnd, showMcatChatErrorModal]);

  // we can use this effect to handle the session related activities
  useEffect(() => {
    if (hasUserOpenedChatDrawer) {
      triggerMcatChatSubjectsRefetch({
        testType: 'MCAT'
      });
    }
  }, [hasUserOpenedChatDrawer]);

  // when externally try to toggle the drawer this will be triggered
  useEffect(() => {
    if (toggleAiMCATDrawerExternally) {
      onToggleDrawer();
      dispatch(setToggleAiMCATDrawerExternally(false));
    }
  }, [toggleAiMCATDrawerExternally]);

  // this use to collapse and expand the left side menu according to side bar behaviour
  useEffect(() => {
    const prevStatus = prevDrawerStatusRef.current;
    const isTransitioningToHidden = prevStatus !== 'hidden' && aiMCATChatDrawerStatus === 'hidden';
    
    if (aiMCATChatDrawerStatus !== "hidden") {
      document.documentElement.style.setProperty(
        "--chat-sidebar-width",
        "549px"
      );
    } else {
      document.documentElement.style.setProperty("--chat-sidebar-width", "0px");
      setChatState("SELECT_SUBJECT");
      setSelectedSubject("");
    }
    // this will indicate new session is start or no
    if (aiMCATChatDrawerStatus === 'open') {
      setHasUserOpenedChatDrawer(true);
    } else if (aiMCATChatDrawerStatus === 'closed') {
      setHasUserOpenedChatDrawer(false);
    } else if (isTransitioningToHidden) {
      // Only run cleanup when transitioning TO hidden (not every render while hidden)
      setHasUserOpenedChatDrawer(false);
      // Cancel any ongoing streaming ONLY when drawer is completely closed (hidden)
      if (cancelStream) {
        cancelStream();
      }
      // Clear messages and reset all chat-related state when drawer is closed
      setMessages([]);
      dispatch(setMcatChatExamInfo({ examName: "", section: "", questionNumber: "" }));
      dispatch(setMcatChatInitialContext(""));
      dispatch(setMcatChatSelectedSubject(""));
    }
    
    // Update the ref for next render
    prevDrawerStatusRef.current = aiMCATChatDrawerStatus;
  }, [aiMCATChatDrawerStatus, cancelStream, dispatch]);

  // this will trigger when there is an error occurred to chat streaming process
  useEffect(() => {
    if (errorChatBubble) {
      const streamingErrorMessage: AIMCATMessage = {
        id: `msg-${Date.now()}-ai`,
        content: `${AI_MCAT_TEXT_TAGS.CHAT_GENERATION_ERROR[0]}A temporary error has occurred. Please end the chat and try again later.${AI_MCAT_TEXT_TAGS.CHAT_GENERATION_ERROR[1]}`,
        isUser: false,
      };
      const messagesList = [...messages];
      messagesList[messages.length - 1] = streamingErrorMessage;
      setIsChatGenerationError(true);
      setMessages(messagesList);
    }
  }, [errorChatBubble]);

  // this use to get the streamed message as chunks
  useEffect(() => {
    if (aiResponse) {
      if (identifyChatTags(aiResponse) === 'ERROR_CHAT_GENERATION') {
        setIsChatGenerationError(true);
      }
      setMessages(prev => prev.map((msg, index) =>
          index === messages.length - 1 ? { ...msg, content: aiResponse } : msg
      ));
          }
  }, [aiResponse]);

  // Auto-scroll to the bottom of the chat when messages change
  useEffect(() => {
    if (
      messages.length > 0 &&
      (isSessionEnd || aiMCATChatDrawerStatus === "open")
    ) {
      const chatBody = document.querySelector(".chat-drawer-body");
      if (chatBody) {
        chatBody.scrollTop = chatBody.scrollHeight;
      }
    }
  }, [messages, isSessionEnd, aiMCATChatDrawerStatus]);

  // Reset to subject selection when manually opening AI chat (not from iframe)
  useEffect(() => {
    const isManualDrawerOpen =
      aiMCATChatDrawerStatus === "open" &&
      !mcatChatSession &&
      !mcatChatSelectedSubject &&
      !mcatChatInitialContext; // guard to avoid overriding pending iframe-triggered sessions

    if (isManualDrawerOpen) {
      setChatState("SELECT_SUBJECT");
      setSelectedSubject("");
      setMessages([]);
      setIsPracticeReviewMode(false); // Disable practice review mode for manual opening
    }
  }, [aiMCATChatDrawerStatus, mcatChatSession, mcatChatSelectedSubject, mcatChatInitialContext]);


  const startNewSessionWithSubject = useCallback(async (subject: string) => {

    if (isLoadingStartConversation) {
      return;
    }

    // For iframe-triggered sessions, guard against duplicate triggers BEFORE resetting state
    // Cancel any ongoing streaming when starting a new conversation
    if (cancelStream) {
      cancelStream();
    }

    setSelectedSubject(subject);
    setChatState("CHAT");

    // Reset session end states when starting a new session
    setIsSessionEnd(false);
    setIsChatGenerationError(false);
    setChatStartTime(new Date());
    setIsChatGenerationError(false);
    
    // Set up initial welcome message
    const welcomeMessage: AIMCATMessage = {
      id: `msg-${Date.now()}-ai`,
      content: '...',
      isUser: false,
    };
    
    setMessages([welcomeMessage]);
    
    // If there's initialContext from iframe, start a new conversation with the API
    if (mcatChatInitialContext) {
      // Set practice review mode flag when opened from iframe
      setIsPracticeReviewMode(true);
      practiceReviewInitialContextRef.current = mcatChatInitialContext;
      
      try {
        
        // Get user info from token
        const payload = decodeToken();
        const userId = payload ? payload.userid : null;
        const productCode = payload ? payload.productcode : null;
        const offeringCode = payload ? payload.offeringcode : null;

        // Start conversation with initialContext
        const response: MCatChatStartConversationResponse = await startConversation({
          subject: subject,
          testType: 'MCAT',
          ProductCode: productCode,
          PSOCode: offeringCode,
          UserGuid: userId,
          initialContext: mcatChatInitialContext
        }).unwrap();

        if (response.conversationGuid && response.status !== 'Error') {
          dispatch(setMcatChatAuthInfo({
            mcatChatSession: response.conversationGuid
          }));
          // Store sessionId if provided, otherwise store null
          dispatch(setMcatChatSessionId(response.sessionId ?? null));
          
          // Send initial empty message to start the conversation
          sendUserMessage({
            Text: "",
            conversationGuid: response.conversationGuid
          });
          
          startChatTracking({
            ConversationGuid: response.conversationGuid
          });
        } else {
          dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
          dispatch(setShowMcatChatErrorModal(true));
          console.error('Failed to start conversation:', response);
        }
        } catch (error) {
          console.error('Error starting AI chat conversation:', error);
          dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
          dispatch(setShowMcatChatErrorModal(true));
        }
      
      // Clear the initialContext after using it
      dispatch(setMcatChatInitialContext(""));
    } else if (mcatChatSession) {
      // If there's already a session but no initialContext, just continue with existing session
      sendUserMessage({
        Text: "",
        conversationGuid: mcatChatSession
      });
      
      startChatTracking({
        ConversationGuid: mcatChatSession
      });
    }
    
    // Clear the Redux subject state after using it
    dispatch(setMcatChatSelectedSubject(""));
  }, [mcatChatSession, mcatChatInitialContext, sendUserMessage, startChatTracking, startConversation, dispatch, isLoadingStartConversation, cancelStream]);

  // Handle iframe-triggered conversation with existing session check
  useEffect(() => {
    if (mcatChatSelectedSubject) {
      // Capture the subject value immediately
      const subjectToProcess = mcatChatSelectedSubject;
      
      // CRITICAL: Clear Redux state immediately to prevent re-triggering on drawer status changes
      // We've captured the value locally, so we don't need it in Redux anymore
      dispatch(setMcatChatSelectedSubject(""));
      
      // If we're already showing transition warning, just update the pending subject
    if (chatState === "TRANSITION_WARNING") {
        // Update pending subject if it's different
        if (pendingSubjectForTransition !== subjectToProcess) {
          setPendingSubjectForTransition(subjectToProcess);
        }
        
        // IMPORTANT: Re-open panel if it was minimized
        if (aiMCATChatDrawerStatus !== "open") {
          dispatch(setAIMCATChatStatus("open"));
          dispatch(setLayout('toggle-desktop-menu'));
        }
        
      return;
    }
    
      // Always ensure the AI panel is open when triggered from iframe
      if (aiMCATChatDrawerStatus !== "open") {
        dispatch(setAIMCATChatStatus("open"));
        dispatch(setLayout('toggle-desktop-menu'));
      }
      
      // Check if there's an active conversation with real messages
      // Exclude the initial welcome message (single AI message with '...' content while loading)
      const isWelcomeMessage = messages.length == 1 && !messages[0].isUser && messages[0].content === '...';
      const hasActiveSession = mcatChatSession && (messages.length > 1 || (!isWelcomeMessage && messages.length == 1));
      
      // Show warning if there's an active session (unless user has disabled warnings)
      // This warns users before starting a new session that will replace their current conversation
      const shouldShowWarning =  !mcatChatDoNotShowTransitionWarning 
        && hasActiveSession;
      
      
      // Check if we should show transition warning
      if (shouldShowWarning) {
        // Show transition warning - user has an active conversation and hasn't disabled warnings
        setPendingSubjectForTransition(subjectToProcess);
        // Store the CURRENT exam info (what's displayed in header now) before we update it
        // Use the ref value which has the OLD exam info before Redux was updated
        setPreviousExamInfoBeforeTransition({
          examName: currentDisplayedExamInfoRef.current.examName,
          section: currentDisplayedExamInfoRef.current.section,
          questionNumber: currentDisplayedExamInfoRef.current.questionNumber
        });
        // Store the current isPracticeReviewMode state before changing it
        setPreviousIsPracticeReviewMode(isPracticeReviewMode);
        // UPDATE the header immediately with the new exam info from message bridge
        // mcatChatExamInfo already has the new values from Redux, so this is already showing
        // but we keep this for clarity and to ensure it's explicit
        dispatch(setMcatChatExamInfo(mcatChatExamInfo));
        // Set isPracticeReviewMode to true so the header displays the exam info
        setIsPracticeReviewMode(true);
        setChatState("TRANSITION_WARNING");
        
        // Note: Redux state (mcatChatSelectedSubject) already cleared above
        // initialContext will be cleared by cancel or start handlers
      } else {
        // No active session or user disabled warnings - proceed directly to start new session
        // Call startNewSessionWithSubject directly to avoid dependency issues
        const startSession = async () => {
          // Check if already loading to prevent duplicates
          if (isLoadingStartConversation) {
            return;
          }
          
          // Call the function directly
          await startNewSessionWithSubject(subjectToProcess);
        };
        
        startSession();
      }
    }
  }, [mcatChatSelectedSubject, selectedSubject, mcatChatSession, chatState, mcatChatDoNotShowTransitionWarning, aiMCATChatDrawerStatus, dispatch, isLoadingStartConversation, startNewSessionWithSubject, pendingSubjectForTransition]);

  // Update ref with header exam info after transition logic has had a chance to snapshot the previous value
  useEffect(() => {
    if (chatState !== "TRANSITION_WARNING") {
      currentDisplayedExamInfoRef.current = {
        examName: mcatChatExamInfo.examName,
        section: mcatChatExamInfo.section,
        questionNumber: mcatChatExamInfo.questionNumber
      };
    }
  }, [mcatChatExamInfo, chatState]);

  // trigger when user changing the subject
  const onChangeSubject = useCallback(
    (subject: string) => {
      if (subject !== selectedSubject) {
        // Cancel any ongoing streaming when starting a new conversation
        if (cancelStream) {
          cancelStream();
        }
        
        // Set practice review mode to false for manual subject selection
        setIsPracticeReviewMode(false);
        // Reset session end states when starting a new session
        setIsSessionEnd(false);
        setIsChatGenerationError(false);

        // when changing the subjects it will always create a new session id in backend
        startConversation({
          subject,
          testType: 'MCAT',
          ProductCode: productCode,
          PSOCode: offeringCode,
          UserGuid: userId,
          // llmVersion: 'gpt-4o'
        })
            .unwrap()
            .then((payload: MCatChatStartConversationResponse) => {
                  if (payload.conversationGuid && payload.status !== 'Error') {
                    dispatch(setMcatChatAuthInfo({
                      mcatChatSession: payload.conversationGuid
                    }));
                    // Store sessionId if provided, otherwise store null
                    dispatch(setMcatChatSessionId(payload.sessionId ?? null));

                setSelectedSubject(subject);
                setChatState("CHAT");
                // Reset session end flags when starting a new session to prevent stale state
                setIsSessionEnd(false);
                setIsChatGenerationError(false);
                setChatStartTime(new Date());

                const welcomeMessage: AIMCATMessage = {
                  id: `msg-${Date.now()}-ai`,
                  content: '...',
                  isUser: false,
                };

                setMessages([welcomeMessage]);
                sendUserMessage({
                  Text: "",
                  conversationGuid: payload.conversationGuid
                })
                startChatTracking({
                  ConversationGuid: payload.conversationGuid
                });

              } else {
                dispatch(setMcatChatAuthInfo({isMcatChatSessionEnd: true}));
                dispatch(setShowMcatChatErrorModal(true));
              }
            }).catch((e: any) => {
              dispatch(setMcatChatAuthInfo({isMcatChatSessionEnd: true}));
              dispatch(setShowMcatChatErrorModal(true));
            });
      }
    },
    [selectedSubject, chatState, messages, cancelStream, startConversation, dispatch, sendUserMessage, startChatTracking, productCode, offeringCode, userId]
  );

  // trigger when user send message to AI bot
  const onSendMessage = useCallback((message: string) => {
      const chatStreamingBody = {
        Text: message,
        conversationGuid: mcatChatSession ?? ''
      };
      
      // Send Mixpanel event with all available data
      mixpanelHelper.track("Send to AI Tutor", {
        userMessage: message,
        conversationGuid: mcatChatSession ?? '',
        testType: 'MCAT',
        userId: userId,
        productCode: productCode,
        pSOCode: offeringCode, // Using exact spelling as requested to match "Receive from AI Tutor"
        sessionId: mcatChatSessionId ?? null, // Safe handling - forwards null if not provided
        chatStreamingBody: chatStreamingBody
      });
      
      sendUserMessage(chatStreamingBody);
      
      const userMessage: AIMCATMessage = {
        id: `msg-${Date.now()}-user`,
        content: message,
        isUser: true,
      };

      const aiMessage: AIMCATMessage = {
        id: `msg-${Date.now()}-ai`,
        content: '...',
        isUser: false,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage, aiMessage]);
    }, [messages, mcatChatSession, sendUserMessage, userId, productCode, offeringCode, mcatChatSessionId]);

  // use to send the feedback to TPR
  const onSendFeedback = (rating: number, feedback: string) => {
    provideFeedback({
      ConversationGuid: mcatChatSession ?? '',
      FeedbackRating: rating,
      FeedbackComments: feedback
    }).unwrap()
        .finally(() => {
          onCloseDrawer();
        })
  }


  // Transition warning handlers
  const handleTransitionWarningStartNewSession = useCallback(() => {
    if (pendingSubjectForTransition) {
      // Redux mcatChatSelectedSubject is already cleared when we entered transition warning
      // Clear initialContext since we're committing to the new session
      dispatch(setMcatChatInitialContext(""));
      
      // Exam info is already in Redux (we updated it when showing the transition warning)
      // isPracticeReviewMode is already set to true from showing the transition warning
      // Just clear the previous states
      setPreviousExamInfoBeforeTransition(null);
      setPreviousIsPracticeReviewMode(false);
      
      // startNewSessionWithSubject will handle sending AI_TRIGGER_DISABLE message to iframe (line 260)
      startNewSessionWithSubject(pendingSubjectForTransition);
      setPendingSubjectForTransition("");
    }
  }, [pendingSubjectForTransition, startNewSessionWithSubject, dispatch]);

  const handleTransitionWarningCancel = useCallback(() => {
    
    // REVERT the header back to what it was before the transition warning
    if (previousExamInfoBeforeTransition) {
      dispatch(setMcatChatExamInfo(previousExamInfoBeforeTransition));
      setPreviousExamInfoBeforeTransition(null);
    } else {
      // If there was no previous exam info, clear it completely
      dispatch(setMcatChatExamInfo({ examName: "", section: "", questionNumber: "" }));
    }
    
    // REVERT isPracticeReviewMode back to what it was before
    setIsPracticeReviewMode(previousIsPracticeReviewMode);
    setPreviousIsPracticeReviewMode(false);
    
    // Go back to CHAT state to show existing conversation
    setChatState("CHAT");
    setPendingSubjectForTransition("");
    // Redux mcatChatSelectedSubject is already cleared when we entered transition warning
    // Only clear initialContext which may still be set
    dispatch(setMcatChatInitialContext(""));
    // Important: Don't send any message or start any new conversation
  }, [dispatch, previousExamInfoBeforeTransition, previousIsPracticeReviewMode]);

  const handleTransitionWarningDoNotShow = useCallback((checked: boolean) => {
    dispatch(setMcatChatDoNotShowTransitionWarning(checked));
  }, [dispatch]);

  // Listen for external cancel signal (e.g., from iframe closing)
  useEffect(() => {
    if (mcatChatCancelPendingTransition) {
      handleTransitionWarningCancel();
      // Reset the flag
      dispatch(setMcatChatCancelPendingTransition(false));
    }
  }, [mcatChatCancelPendingTransition, handleTransitionWarningCancel, dispatch]);

  // use to close the drawer
    const onCloseDrawer = () => {

        dispatch(setAIMCATChatStatus("hidden"));
        dispatch(setLayout(null));

        // Reset practice review mode and exam info when actually closing the drawer
        setIsPracticeReviewMode(false);
        dispatch(setMcatChatExamInfo({ examName: "", section: "", questionNumber: "" }));

        if (chatState === "TRANSITION_WARNING") {
            // Handle close during transition warning - cancel the pending session and close
            handleTransitionWarningCancel();
        } else if (chatState === "CHAT") {
            if (!isSessionEnd) endChatTracking(getChatEndBodyData(1));
        }

        setChatState("SELECT_SUBJECT");
        resetState();
  };

  // use to start a new chat
  const onClickNewChat = () => {
    if (chatState !== "SELECT_SUBJECT") {
      if (!isSessionEnd) endChatTracking(getChatEndBodyData(1));
      
      dispatch(setMcatChatSelectedSubject(""));
      dispatch(setMcatChatInitialContext(""));
      
      setPendingSubjectForTransition("");

      if (isPracticeReviewMode) {
        // In practice review mode, restart with the same subject and exam info;
        // do not clear isPracticeReviewMode or mcatChatExamInfo
        const subjectToRestart = selectedSubject;
        resetState();
        startConversation({
          subject: subjectToRestart,
          testType: 'MCAT',
          ProductCode: productCode,
          PSOCode: offeringCode,
          UserGuid: userId,
          initialContext: practiceReviewInitialContextRef.current || undefined,
        })
          .unwrap()
          .then((payload: MCatChatStartConversationResponse) => {
            if (payload.conversationGuid && payload.status !== 'Error') {
              dispatch(setMcatChatAuthInfo({ mcatChatSession: payload.conversationGuid }));
              dispatch(setMcatChatSessionId(payload.sessionId ?? null));
              setSelectedSubject(subjectToRestart);
              setChatState("CHAT");
              setIsSessionEnd(false);
              setIsChatGenerationError(false);
              setChatStartTime(new Date());
              setMessages([{ id: `msg-${Date.now()}-ai`, content: '...', isUser: false }]);
              sendUserMessage({ Text: "", conversationGuid: payload.conversationGuid });
              startChatTracking({ ConversationGuid: payload.conversationGuid });
            } else {
              dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
              dispatch(setShowMcatChatErrorModal(true));
            }
          })
          .catch(() => {
            dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
            dispatch(setShowMcatChatErrorModal(true));
          });
      } else {
        setChatState("SELECT_SUBJECT");
        resetState();
      }
    }
  };

  // use to toggle the drawer like expand and collapse
  const onToggleDrawer = () => {
    if (aiMCATChatDrawerStatus === "closed") {
      if (chatState === 'CHAT') {
        if (chatDrawerBodyRef?.current) {
          setTimeout(() => {
            chatDrawerBodyRef.current.scrollTo({
              top: lastScrollPositionWhenCollapsed,
              behavior: 'instant',
              block: 'center'
            });
          }, 100)
        }
      }
      dispatch(setAIMCATChatStatus("open"));
      dispatch(setLayout("toggle-desktop-menu"));
    } else {
      if (chatState === "CHAT") {
        setLastScrollPositionWhenCollapsed(chatDrawerBodyRef?.current?.scrollTop ?? 0);
      }
      dispatch(setAIMCATChatStatus("closed"));
      dispatch(setLayout(null));
    }
  };

  // use to identify the errors and other indication using text
  const identifyChatTags = (text: string): AI_MCAT_CHAT_MESSAGE_TYPE => {
    if (text.includes(AI_MCAT_TEXT_TAGS.CHAT_GENERATION_ERROR[0]) || text.includes(AI_MCAT_TEXT_TAGS.CHAT_GENERATION_ERROR[1])) {
      return "ERROR_CHAT_GENERATION";
    } else {
      return "";
    }
  };

  // this function returns the chat end tracking request body information
  const getChatEndBodyData = (endReason: 1 | 2) : MCatChatEndTrackingRequest => {
    return {
      ConversationGuid: mcatChatSession ?? '',
      Duration: Math.floor((new Date().getTime() - (chatStartTime?.getTime() ?? 0)) / 1000),
      EndReason: endReason
    }
  }

  // use to remove the text related identifiers. Ex: use <Error> tag to identify the text has an error.
  // when rendering we can identify and remove it, but still can show a message about there is an error
  const fineTuneText = (text: string) => {
    Object.values(AI_MCAT_TEXT_TAGS).forEach(textTag => {
      textTag.forEach(tag => {
        if (text.includes(tag)) {
          text = text.replace(tag, "");
        }
      })
    })
    return text;
  }

  // this use to reset the whole state. when closing drawer or some action
  const resetState = () => {
    setSelectedSubject("");
    setMessages([]);
    setLastScrollPositionWhenCollapsed(0);
    setChatStartTime(null);
    setIsChatGenerationError(false)
    setIsSessionEnd(false);
    // NOTE: Don't reset isPracticeReviewMode here - it should persist to the feedback screen
    
    // Clear Redux states for session and conversation
    dispatch(setMcatChatAuthInfo({
      mcatChatSession: "",
      isMcatChatSessionEnd: false
    }));
    dispatch(setShowMcatChatErrorModal(false));
    
    // Clear Redux states related to practice review and iframe interactions
    // These are cleared here to ensure consistency when resetState is called from various places
    dispatch(setMcatChatSelectedSubject(""));
    dispatch(setMcatChatInitialContext(""));
    // Note: mcatChatExamInfo is not cleared here to preserve it for the feedback screen in practice review mode
  }

  // this use to detect is user being idle
  const onIdle = () => {
    setIsSessionEnd(true);
    endChatTracking(getChatEndBodyData(2));
  };

  // this displays at the top of screen after selecting a subject
  const getSubtitle = () => {
    // Remove the Princeton Review tagline since it's now in the main header
    return undefined;
  };

  // use to render the chat window content. initially subjects, after select a subject chat list, after that
  // review section
  const renderChatContent = () => {
    if (chatState === "SELECT_SUBJECT" && mcatChatSubjectsRefetchResult?.data && mcatChatSubjectsRefetchResult.data.length > 0) {
      return (
          <SubjectSelector
              subjects={mcatChatSubjectsRefetchResult.data.map((subjectObj) => subjectObj.subject)}
              selectedSubject={selectedSubject}
              onSubjectChange={onChangeSubject}
          />
      );
    } else if (chatState === "TRANSITION_WARNING") {
      return (
          <TransitionWarning
              theme={theme}
              onStartNewSession={handleTransitionWarningStartNewSession}
              onCancel={handleTransitionWarningCancel}
              onDoNotShowAgain={handleTransitionWarningDoNotShow}
          />
      );
    } else if (chatState === "FEEDBACK") {
      return (
          <ReviewSection
              theme={theme}
              onClose={onCloseDrawer}
              onSend={onSendFeedback}
              disabled={isPracticeReviewMode}
          />
      )
    } else {
      return (
        <>
          <div className="chat-messages">
            {/* Exam info now shows in ChatDrawerHeader blue box */}
            {messages.map((message, index) => (
              <ChatBubble
                key={message.id}
                isUser={message.isUser}
                theme={theme}
                isLoading={!message.isUser && message.content === '...' && (index === messages.length - 1 || index === 0)}
                isErrorChatGeneration={identifyChatTags(message.content) === 'ERROR_CHAT_GENERATION'}
                message={fineTuneText(message.content)}
              />
            ))}
            {
              isChatGenerationError &&
                <div className='error-chat-end-button'>
                  <Button
                      label="End Chat"
                      icon={false}
                      type="score"
                      styles="ai-review-btn "
                      onClick={onCloseDrawer}
                  />
                </div>
            }
            {isSessionEnd && !isChatGenerationError && (
              <ChatIdleMessage
                  onClose={onCloseDrawer}
                  onNewChat={onClickNewChat}
              />
            )}
          </div>
        </>
      );
    }
  };

  const renderFooter = () => {
    if (chatState === "CHAT" && !isSessionEnd && !isChatGenerationError) {
      return (
          <ChatDrawerFooter
              theme={theme}
              isLoadingPreviousChat={isLoadingChatBubble}
              onSend={onSendMessage}
          />
      )
    }
    // Hide footer for transition warning and other states
    return null;
  };

  const renderChatIdleTimer = () => {
    if (messages?.length > 0 && !isSessionEnd) {
      return (
          <IdleTimer
              timeout={3600000}
              timerRef={chatSideDrawerRef}
              onIdle={onIdle}
          />
      )
    }
    return null;
  };
  
  const handleErrorModalClose = () => {
    onCloseDrawer();
  };

  return (
      <>
        <AIMCATErrorAlert
            isOpen={showMcatChatErrorModal ?? false}
            theme={theme}
            onClose={handleErrorModalClose}
        />
        {
          aiMCATChatDrawerStatus !== "hidden" && (
              <>
                <ChatSideDrawer
                    isOpen={aiMCATChatDrawerStatus === "open"}
                    onToggle={onToggleDrawer}
                    onClose={onCloseDrawer}
                    drawerRef={chatSideDrawerRef}
                    drawerClassName="chat-drawer-container"
                    theme={theme}
                >
                  <div
                      className={`ai-mcat-chat-container ${isMobile ? "mobile-view" : ""}`}
                  >
                    {
                      isLoadingStartConversation ||
                      isSubjectsLoading ||
                      isLoadingStartChatTracking ||
                      isLoadingEndChatTracking ||
                      isLoadingProvideFeedback?
                          <Spinner theme={theme} />
                          :
                          <>
                            <div className="chat-header-actions">
                              {isMobile && (
                                  <div className="mobile-header-container">
                                    <div className="mobile-logo">
                                      <img 
                                        src={theme === 'dark' ? McatChatLogoDark : McatChatLogo} 
                                        alt="MCAT Chat Logo"
                                        key={theme}
                                      />
                                    </div>
                                    <div className="mobile-title">
                                      <Typography variant="h6Bold" label="AI Tutor" />
                                    </div>
                                  </div>
                              )}

                              <Typography variant="h10Medium" label="BETA" styles="beta-badge" />

                              {
                                chatState !== "FEEDBACK" ?
                                    <div className="chat-header-actions-buttons">
                                      {!isMobile && (
                                          <>
                                            <button className="close-button" onClick={onCloseDrawer} data-tracking-id={AI_CHAT_CLOSE}>
                                              <span className="close-icon">×</span>
                                            </button>
                                          </>
                                      )}
                                    </div> : null
                              }
                            </div>
                            {isMobile && (chatState === 'CHAT' || chatState === 'SELECT_SUBJECT') && (
                                <div className="mobile-header-btn-container">
                                  <Button
                                      label="Close"
                                      type="score"
                                      icon={false}
                                      onClick={onCloseDrawer}
                                      styles="header-btn"
                                  />
                                </div>
                            )}

                            {/* Only show the ChatDrawerHeader if not mobile or if we're not in subject selection */}
                            {(!isMobile || chatState !== "SELECT_SUBJECT") && (
                                <ChatDrawerHeader
                                    title="AI Tutor"
                                    subtitle={getSubtitle()}
                                    selectedSubject={selectedSubject}
                                    isMobile={isMobile}
                                    theme={theme}
                                    examName={mcatChatExamInfo.examName}
                                    section={mcatChatExamInfo.section}
                                    questionNumber={mcatChatExamInfo.questionNumber}
                                    isPracticeReviewMode={isPracticeReviewMode}
                                />
                            )}
                            {/* here define the chat list content*/}
                            <ChatDrawerBody
                                scrollRef={chatDrawerBodyRef}
                            >
                              {renderChatContent()}
                            </ChatDrawerBody>
                            {renderFooter()}
                          </>
                    }
                  </div>
                </ChatSideDrawer>
                {renderChatIdleTimer()}
              </>)
        }
      </>
  )
};

export default AIMCATChat;
